/**
 * Late fees on formal loans, configured per loan type. The server charges them (one per missed due
 * date, on everything overdue at that moment, never on earlier fees); this file only builds the form
 * values and explains a rule in words.
 */
import * as Yup from "yup";
import { formatNaira } from "../../utils/dashboardFormat";

export const LATE_FEE_TYPE_OPTIONS = [
  { value: "", label: "No late fee" },
  { value: "PERCENT", label: "Percentage of the overdue amount" },
  { value: "FIXED", label: "Fixed amount" },
];

export function lateFeeInitialValues(loanType) {
  return {
    late_fee_type: loanType?.late_fee_type ?? "",
    late_fee_value: loanType?.late_fee_value ?? "",
    late_fee_grace_days: loanType?.late_fee_grace_days ?? 0,
    late_fee_cap: loanType?.late_fee_cap ?? "",
  };
}

export const lateFeeValidation = {
  late_fee_type: Yup.string().nullable(),
  late_fee_value: Yup.number()
    .nullable()
    .transform((value, original) => (original === "" || original == null ? null : Number(original)))
    .test("late-fee-value", "Enter a fee greater than 0", function (value) {
      return !this.parent.late_fee_type || Number(value) > 0;
    })
    .test("late-fee-percent", "A percentage can not be more than 100", function (value) {
      return this.parent.late_fee_type !== "PERCENT" || value == null || Number(value) <= 100;
    }),
  late_fee_grace_days: Yup.number()
    .nullable()
    .transform((value, original) => (original === "" || original == null ? 0 : Number(original)))
    .integer("Use whole days")
    .min(0, "Must be 0 or more")
    .max(365, "Must be 365 or less"),
  late_fee_cap: Yup.number()
    .nullable()
    .transform((value, original) => (original === "" || original == null ? null : Number(original)))
    .min(0.01, "Must be greater than 0"),
};

/** Fields to send; an empty type switches the rule off. */
export function lateFeeToPayload(values) {
  if (!values.late_fee_type) {
    return { late_fee_type: null, late_fee_value: null, late_fee_grace_days: 0, late_fee_cap: null };
  }
  return {
    late_fee_type: values.late_fee_type,
    late_fee_value: Number(values.late_fee_value),
    late_fee_grace_days: Number(values.late_fee_grace_days || 0),
    late_fee_cap: values.late_fee_cap === "" || values.late_fee_cap == null ? null : Number(values.late_fee_cap),
  };
}

/** One sentence describing a rule as the API returns it ({type, value, grace_days, cap}). */
export function describeLateFeeRule(rule) {
  if (!rule || !rule.type) return "";
  const amount = rule.type === "PERCENT" ? `${Number(rule.value)}% of the overdue amount` : formatNaira(rule.value);
  const parts = [`${amount} for each missed due date`];
  if (Number(rule.grace_days) > 0) {
    parts.push(`after ${rule.grace_days} day${Number(rule.grace_days) === 1 ? "" : "s"} grace`);
  }
  if (rule.cap != null) parts.push(`up to ${formatNaira(rule.cap)} in total`);
  return parts.join(", ");
}
